"use client";
import { Suspense, useEffect, useMemo, useState } from "react";
import Breadcrumb from "../Common/Breadcrumb";
import FacetDropdown from "./FacetDropdown";
import ClearFilters from "./ClearFilters";
import RadixSlider from "./RadixSlider";
import {
  FourSquaresIcon,
  SidebarToggleIcon,
  TwoSquaresIcon,
} from "@/assets/icons";
import { Product } from "@/types/product";
import SingleListItem from "../Shop/SingleListItem";
import SingleGridItem from "../Shop/SingleProductItem";
import ProductsEmptyState from "./ProductsEmptyState";
import TopBar from "./TopBar";
import Pagination from "../Common/Pagination";
import { VendureFacet } from "@/lib/vendure/models";
import { useAppSelector } from "@/redux/store";
import { setSidebar } from "@/redux/features/sidebar-slice";
import { useDispatch } from "react-redux";
import { useSearchParams, useRouter } from "next/navigation";
import NProgress from "nprogress";
import { NexusCategoryConfig } from "@/types/nexus-product";
import GraphicCategoryNav from "@/components/GraphicCategoryNav";

type GraphicCategory = NexusCategoryConfig & {
  image: string;
  productCount: number;
};

type PropsType = {
  data: {
    products: Product[];
    allProductsCount: number;
    highestPrice: number;
    host: string | null;
    facets: VendureFacet[];
    facetsCount: Record<string, Record<string, number>>;
    currentPage: number;
    pageSize: number;
    graphicCategories?: GraphicCategory[];
  };
};

const ShopWithSidebar = ({ data }: PropsType) => {
  const {
    products,
    allProductsCount,
    highestPrice,
    host,
    facets,
    facetsCount,
    pageSize,
    currentPage,
    graphicCategories,
  } = data;

  const dispatch = useDispatch();
  const productSidebar = useAppSelector((state) => state.sidebarReducer.isOpen);
  const [productStyle, setProductStyle] = useState("grid");
  const [stickyMenu, setStickyMenu] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();

  const handleStickyMenu = () => {
    if (window.scrollY >= 80) {
      setStickyMenu(true);
    } else {
      setStickyMenu(false);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleStickyMenu);

    if (productSidebar && window.innerWidth < 1280) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    // closing sidebar while clicking outside
    function handleClickOutside(event: any) {
      if (!event.target.closest(".sidebar-content")) {
        dispatch(setSidebar(false));
      }
    }

    if (productSidebar) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "";
    };
  }, [productSidebar, dispatch]);

  const handlePageChange = (page: number) => {
    if (page === currentPage) return;

    NProgress.start();
    const params = new URLSearchParams(searchParams?.toString());
    params.set("page", String(page));
    router.push(`?${params.toString()}`, { scroll: false });
    requestAnimationFrame(() =>
      window.scrollTo({ top: 0, behavior: "smooth" }),
    );
  };

  useEffect(() => {
    NProgress.done();
  }, [searchParams]);

  return (
    <>
      {/*<Breadcrumb*/}
      {/*  title={"Explore All Products"}*/}
      {/*  pages={["shop", "/", "shop with sidebar"]}*/}
      {/*/>*/}

      <section className="relative pt-10 pb-20 overflow-hidden lg:pt-12 xl:pt-16 bg-gray-2">
        <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 xl:px-0">
          <div className="flex gap-7.5">
            {/* Sidebar Start */}
            <div
              className={`sidebar-content fixed xl:z-1 z-9999 left-0 top-0 xl:translate-x-0 xl:static xl:w-1/4 w-full ease-out duration-200 ${
                productSidebar ? "translate-x-0 bg-white" : "-translate-x-full"
              }`}
            >
              <div className="flex flex-col gap-6 overflow-y-auto max-xl:h-screen max-xl:p-5">
                <button
                  onClick={() => dispatch(setSidebar(false))}
                  className="xl:hidden self-end -mt-1 -mr-1 p-2 text-dark hover:text-blue transition-colors"
                  aria-label="Close filters"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>

                {/* filter box */}
                <ClearFilters />

                {facets.map((facet) => (
                  <Suspense key={facet.id}>
                    <FacetDropdown facet={facet} facetsCount={facetsCount} />
                  </Suspense>
                ))}
              </div>
            </div>
            {/* Sidebar End */}

            {/* Content Start */}
            <div className="w-full xl:w-3/4">
              <div className="rounded-lg bg-white shadow-1 pl-3 pr-2.5 py-2.5 mb-6">
                <div className="flex items-center justify-between">
                  {/* top bar left */}
                  <TopBar
                    allProductsCount={allProductsCount}
                    showingProductsCount={products.length}
                  />

                  {/* top bar right */}
                  <div className="flex items-center gap-2.5 shrink-0">
                    <button
                      onClick={() => setProductStyle("grid")}
                      aria-label="button for product grid tab"
                      className={`${
                        productStyle === "grid"
                          ? "bg-blue border-blue text-white"
                          : "text-dark bg-gray-1 border-gray-3"
                      } flex items-center justify-center w-10.5 h-9 rounded-lg border ease-out duration-200 hover:bg-blue hover:border-blue hover:text-white`}
                    >
                      <FourSquaresIcon />
                    </button>

                    <button
                      onClick={() => setProductStyle("list")}
                      aria-label="button for product list tab"
                      className={`${
                        productStyle === "list"
                          ? "bg-blue border-blue text-white"
                          : "text-dark bg-gray-1 border-gray-3"
                      } flex items-center justify-center w-10.5 h-9 rounded-lg border ease-out duration-200 hover:bg-blue hover:border-blue hover:text-white`}
                    >
                      <TwoSquaresIcon />
                    </button>
                  </div>
                </div>
              </div>

              {graphicCategories && graphicCategories.length > 0 && (
                <Suspense>
                  <GraphicCategoryNav categories={graphicCategories} />
                </Suspense>
              )}

              {/* Products Grid Tab Content Start */}

              {products.length ? (
                <div
                  className={
                    productStyle === "grid"
                      ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-3 gap-x-4 gap-y-9 sm:gap-x-7.5"
                      : "flex flex-col gap-7.5"
                  }
                >
                  {products.map((product) => {
                    return productStyle === "grid" ? (
                      <SingleGridItem
                        key={product._id}
                        item={product}
                        host={host}
                      />
                    ) : (
                      <SingleListItem
                        key={product._id}
                        item={product}
                        host={host}
                      />
                    );
                  })}
                </div>
              ) : (
                <ProductsEmptyState />
              )}

              <Pagination
                currentPage={currentPage}
                totalCount={allProductsCount}
                pageSize={pageSize}
                onPageChange={handlePageChange}
              />
            </div>

            {/* Content End */}
          </div>
        </div>
      </section>
    </>
  );
};

export default ShopWithSidebar;
